import React, { useState } from 'react';
import Icon from '../Icon';
import Button from '../ui/Button';
import { Modal } from '../ui/Modal';
import { FormInput, FormSelect, FormTextarea } from '../FormField';
import { useConfirm } from '../ui/ConfirmProvider';
import { useNotifications } from '../NotificationProvider';
import { CURRENCY_OPTIONS } from '../../utils/currency';
import { formatDecimal } from '../../utils/decimal';
import { procurementErrorMessage } from '../../utils/procurementErrors';
import { SHIPMENT_MODE_OPTIONS } from './procurementLabels';
import ProcSection from './ProcSection';
import {
  createFee, createRate, deleteFee, deleteRate, updateFee, updateRate, type FeeInput, type RateInput,
} from '../../services/ShippingAgentService';
import type { FeeBasis, ShippingAgent, ShippingAgentFee, ShippingAgentRate } from '../../types/Procurement';

const BASIS_OPTIONS: Array<{ value: FeeBasis; label: string }> = [
  { value: 'flat', label: 'Flat' },
  { value: 'per_collection', label: 'Per collection' },
  { value: 'per_carton', label: 'Per carton' },
  { value: 'per_cbm', label: 'Per CBM' },
  { value: 'per_kg', label: 'Per kg' },
  { value: 'per_shipment', label: 'Per shipment' },
  { value: 'percent_of_value', label: 'Percent of value' },
];

const str = (v: unknown): string => (v === null || v === undefined ? '' : String(v));
const orNull = (v: string): string | null => (v.trim() === '' ? null : v.trim());

type RateDraft = Record<'id' | 'destination' | 'office_label' | 'shipping_mode' | 'min_chargeable_cbm' | 'effective_from' | 'effective_to' | 'currency' | 'rate_per_cbm' | 'rate_per_kg' | 'pickup_fee' | 'notes', string>;
type FeeDraft = Record<'id' | 'rate_id' | 'fee_type' | 'label' | 'charge_basis' | 'applies_below_cbm' | 'applies_above_cbm' | 'office_label' | 'effective_from' | 'effective_to' | 'currency' | 'amount' | 'notes', string>;

const rateDraft = (r?: ShippingAgentRate, agent?: ShippingAgent): RateDraft => ({
  id: r?.id ?? '', destination: str(r?.destination), office_label: str(r?.office_label), shipping_mode: r?.shipping_mode ?? agent?.shipping_mode ?? 'sea',
  min_chargeable_cbm: str(r?.min_chargeable_cbm), effective_from: str(r?.effective_from).slice(0, 10), effective_to: str(r?.effective_to).slice(0, 10),
  currency: str(r?.currency ?? agent?.currency), rate_per_cbm: str(r?.rate_per_cbm), rate_per_kg: str(r?.rate_per_kg), pickup_fee: str(r?.pickup_fee), notes: str(r?.notes),
});

const feeDraft = (f?: ShippingAgentFee, agent?: ShippingAgent): FeeDraft => ({
  id: f?.id ?? '', rate_id: str(f?.rate_id), fee_type: str(f?.fee_type), label: str(f?.label), charge_basis: f?.charge_basis ?? 'flat',
  applies_below_cbm: str(f?.applies_below_cbm), applies_above_cbm: str(f?.applies_above_cbm), office_label: str(f?.office_label),
  effective_from: str(f?.effective_from).slice(0, 10), effective_to: str(f?.effective_to).slice(0, 10), currency: str(f?.currency ?? agent?.currency), amount: str(f?.amount), notes: str(f?.notes),
});

export const rateInputFrom = (d: RateDraft): RateInput => ({
  destination: d.destination.trim(), office_label: orNull(d.office_label), shipping_mode: d.shipping_mode as RateInput['shipping_mode'],
  min_chargeable_cbm: orNull(d.min_chargeable_cbm), effective_from: orNull(d.effective_from), effective_to: orNull(d.effective_to),
  currency: orNull(d.currency), rate_per_cbm: orNull(d.rate_per_cbm), rate_per_kg: orNull(d.rate_per_kg), pickup_fee: orNull(d.pickup_fee), notes: orNull(d.notes),
});

export const feeInputFrom = (d: FeeDraft): FeeInput => ({
  rate_id: orNull(d.rate_id), fee_type: d.fee_type.trim(), label: orNull(d.label), charge_basis: d.charge_basis as FeeBasis,
  applies_below_cbm: orNull(d.applies_below_cbm), applies_above_cbm: orNull(d.applies_above_cbm), office_label: orNull(d.office_label),
  effective_from: orNull(d.effective_from), effective_to: orNull(d.effective_to), currency: orNull(d.currency), amount: orNull(d.amount), notes: orNull(d.notes),
});

interface Props {
  agent: ShippingAgent;
  canEdit: boolean;
  /** Re-read the agent after a change. */
  onChanged: () => Promise<void>;
}

/** Rate cards (per destination) and extra fees of one agent. Money-only: the caller leaves this out for a contractor. */
const ShippingAgentRatesFees: React.FC<Props> = ({ agent, canEdit, onChanged }) => {
  const confirm = useConfirm();
  const { showSuccess, showError } = useNotifications();
  const [rate, setRate] = useState<RateDraft | null>(null);
  const [fee, setFee] = useState<FeeDraft | null>(null);
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const rates = agent.rates ?? [];
  const fees = agent.fees ?? [];

  const saveRate = async () => {
    if (!rate) return;
    const input = rateInputFrom(rate);
    if (!input.destination) { setProblem('Enter the destination this rate is for.'); return; }
    if ((input.rate_per_cbm || input.rate_per_kg || input.pickup_fee) && !input.currency) { setProblem('Choose the currency of these amounts.'); return; }
    setSaving(true); setProblem(null);
    try {
      if (rate.id) await updateRate(agent.id, rate.id, input); else await createRate(agent.id, input);
      showSuccess('Rate saved'); setRate(null); await onChanged();
    } catch (err) { setProblem(procurementErrorMessage(err, 'The rate could not be saved.')); } finally { setSaving(false); }
  };

  const saveFee = async () => {
    if (!fee) return;
    const input = feeInputFrom(fee);
    if (!input.fee_type) { setProblem('Enter what kind of fee this is.'); return; }
    if (input.amount && !input.currency) { setProblem('Choose the currency of the amount.'); return; }
    setSaving(true); setProblem(null);
    try {
      if (fee.id) await updateFee(agent.id, fee.id, input); else await createFee(agent.id, input);
      showSuccess('Fee saved'); setFee(null); await onChanged();
    } catch (err) { setProblem(procurementErrorMessage(err, 'The fee could not be saved.')); } finally { setSaving(false); }
  };

  const remove = async (what: 'rate' | 'fee', id: string, label: string) => {
    const ok = await confirm({ title: `Delete this ${what}?`, subject: label, confirmLabel: 'Delete', variant: 'danger' });
    if (!ok) return;
    try {
      if (what === 'rate') await deleteRate(agent.id, id); else await deleteFee(agent.id, id);
      showSuccess(`${what === 'rate' ? 'Rate' : 'Fee'} deleted`);
      await onChanged();
    } catch (err) { showError(procurementErrorMessage(err, `The ${what} could not be deleted.`)); }
  };

  const currencyOptions = [{ value: '', label: 'Choose a currency' }, ...CURRENCY_OPTIONS];

  return (
    <>
      <ProcSection
        title="Rate cards"
        action={canEdit ? <Button variant="secondary" size="sm" icon={<Icon name="plus" size={14} />} onClick={() => { setProblem(null); setRate(rateDraft(undefined, agent)); }}>Add rate</Button> : undefined}
      >
        {rates.length === 0 ? <p className="text-subtle">No per-destination rates recorded. The agent-level rate applies.</p> : (
          <div className="proc-scroll">
            <table className="proc-table">
              <thead><tr><th>Destination</th><th>Office</th><th>Mode</th><th className="proc-num">Per CBM</th><th className="proc-num">Per kg</th><th className="proc-num">Pickup fee</th><th>From</th>{canEdit && <th aria-label="Actions" />}</tr></thead>
              <tbody>
                {rates.map(r => (
                  <tr key={r.id}>
                    <td>{r.destination}</td><td>{r.office_label ?? '—'}</td><td>{r.shipping_mode}</td>
                    <td className="proc-num">{formatDecimal(r.rate_per_cbm, r.currency)}</td>
                    <td className="proc-num">{formatDecimal(r.rate_per_kg, r.currency)}</td>
                    <td className="proc-num">{formatDecimal(r.pickup_fee, r.currency)}</td>
                    <td>{r.effective_from ?? '—'}</td>
                    {canEdit && (
                      <td className="proc-row-actions">
                        <button type="button" className="btn-icon" title="Edit rate" aria-label={`Edit rate for ${r.destination}`} onClick={() => { setProblem(null); setRate(rateDraft(r, agent)); }}><Icon name="edit" size={14} /></button>
                        <button type="button" className="btn-icon dt-row-action--danger" title="Delete rate" aria-label={`Delete rate for ${r.destination}`} onClick={() => void remove('rate', r.id, r.destination)}><Icon name="trash" size={14} /></button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </ProcSection>

      <ProcSection
        title="Fees"
        action={canEdit ? <Button variant="secondary" size="sm" icon={<Icon name="plus" size={14} />} onClick={() => { setProblem(null); setFee(feeDraft(undefined, agent)); }}>Add fee</Button> : undefined}
      >
        <p className="text-subtle">No rows means none recorded, not none charged.</p>
        {fees.length > 0 && (
          <div className="proc-scroll">
            <table className="proc-table">
              <thead><tr><th>Fee</th><th>Basis</th><th className="proc-num">Amount</th><th>Applies</th><th>Office</th>{canEdit && <th aria-label="Actions" />}</tr></thead>
              <tbody>
                {fees.map(f => (
                  <tr key={f.id}>
                    <td>{f.label || f.fee_type}</td>
                    <td>{BASIS_OPTIONS.find(b => b.value === f.charge_basis)?.label ?? f.charge_basis}</td>
                    <td className="proc-num">{formatDecimal(f.amount, f.currency)}</td>
                    <td>{[f.applies_below_cbm != null ? `below ${f.applies_below_cbm} CBM` : '', f.applies_above_cbm != null ? `above ${f.applies_above_cbm} CBM` : ''].filter(Boolean).join(', ') || 'Always'}</td>
                    <td>{f.office_label ?? '—'}</td>
                    {canEdit && (
                      <td className="proc-row-actions">
                        <button type="button" className="btn-icon" title="Edit fee" aria-label={`Edit fee ${f.label || f.fee_type}`} onClick={() => { setProblem(null); setFee(feeDraft(f, agent)); }}><Icon name="edit" size={14} /></button>
                        <button type="button" className="btn-icon dt-row-action--danger" title="Delete fee" aria-label={`Delete fee ${f.label || f.fee_type}`} onClick={() => void remove('fee', f.id, f.label || f.fee_type)}><Icon name="trash" size={14} /></button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </ProcSection>

      <Modal
        open={!!rate} onClose={() => setRate(null)} title={rate?.id ? 'Edit rate' : 'Add rate'} size="small"
        footer={<div className="modal-actions"><Button variant="primary" loading={saving} onClick={() => void saveRate()}>Save rate</Button><Button variant="secondary" onClick={() => setRate(null)}>Cancel</Button></div>}
      >
        {rate && (
          <>
            <div className="form-row">
              <FormInput label="Destination" required value={rate.destination} onChange={e => setRate({ ...rate, destination: e.target.value })} />
              <FormInput label="Office" value={rate.office_label} onChange={e => setRate({ ...rate, office_label: e.target.value })} />
            </div>
            <div className="form-row">
              <FormSelect label="Mode" value={rate.shipping_mode} onChange={e => setRate({ ...rate, shipping_mode: e.target.value })} options={SHIPMENT_MODE_OPTIONS} />
              <FormSelect label="Currency" value={rate.currency} onChange={e => setRate({ ...rate, currency: e.target.value })} options={currencyOptions} />
            </div>
            <div className="form-row">
              <FormInput label="Rate per CBM" inputMode="decimal" value={rate.rate_per_cbm} onChange={e => setRate({ ...rate, rate_per_cbm: e.target.value })} />
              <FormInput label="Rate per kg" inputMode="decimal" value={rate.rate_per_kg} onChange={e => setRate({ ...rate, rate_per_kg: e.target.value })} />
            </div>
            <div className="form-row">
              <FormInput label="Pickup fee" inputMode="decimal" value={rate.pickup_fee} onChange={e => setRate({ ...rate, pickup_fee: e.target.value })} />
              <FormInput label="Minimum chargeable CBM" inputMode="decimal" value={rate.min_chargeable_cbm} onChange={e => setRate({ ...rate, min_chargeable_cbm: e.target.value })} />
            </div>
            <div className="form-row">
              <FormInput label="Effective from" type="date" value={rate.effective_from} onChange={e => setRate({ ...rate, effective_from: e.target.value })} />
              <FormInput label="Effective to" type="date" value={rate.effective_to} onChange={e => setRate({ ...rate, effective_to: e.target.value })} />
            </div>
            <FormTextarea label="Notes" rows={2} value={rate.notes} onChange={e => setRate({ ...rate, notes: e.target.value })} />
            {problem && <p role="alert" className="form-error-text">{problem}</p>}
          </>
        )}
      </Modal>

      <Modal
        open={!!fee} onClose={() => setFee(null)} title={fee?.id ? 'Edit fee' : 'Add fee'} size="small"
        footer={<div className="modal-actions"><Button variant="primary" loading={saving} onClick={() => void saveFee()}>Save fee</Button><Button variant="secondary" onClick={() => setFee(null)}>Cancel</Button></div>}
      >
        {fee && (
          <>
            <div className="form-row">
              <FormInput label="Fee type" required help="For example packing, gate or handling" value={fee.fee_type} onChange={e => setFee({ ...fee, fee_type: e.target.value })} />
              <FormInput label="Label" value={fee.label} onChange={e => setFee({ ...fee, label: e.target.value })} />
            </div>
            <div className="form-row">
              <FormSelect label="Charged" value={fee.charge_basis} onChange={e => setFee({ ...fee, charge_basis: e.target.value })} options={BASIS_OPTIONS} />
              <FormSelect label="Currency" value={fee.currency} onChange={e => setFee({ ...fee, currency: e.target.value })} options={currencyOptions} />
              <FormInput label="Amount" inputMode="decimal" value={fee.amount} onChange={e => setFee({ ...fee, amount: e.target.value })} />
            </div>
            <div className="form-row">
              <FormSelect label="Only for rate" value={fee.rate_id} onChange={e => setFee({ ...fee, rate_id: e.target.value })} options={[{ value: '', label: 'Any rate' }, ...rates.map(r => ({ value: r.id, label: r.destination }))]} />
              <FormInput label="Office" value={fee.office_label} onChange={e => setFee({ ...fee, office_label: e.target.value })} />
            </div>
            <div className="form-row">
              <FormInput label="Applies below (CBM)" inputMode="decimal" value={fee.applies_below_cbm} onChange={e => setFee({ ...fee, applies_below_cbm: e.target.value })} />
              <FormInput label="Applies above (CBM)" inputMode="decimal" value={fee.applies_above_cbm} onChange={e => setFee({ ...fee, applies_above_cbm: e.target.value })} />
            </div>
            <div className="form-row">
              <FormInput label="Effective from" type="date" value={fee.effective_from} onChange={e => setFee({ ...fee, effective_from: e.target.value })} />
              <FormInput label="Effective to" type="date" value={fee.effective_to} onChange={e => setFee({ ...fee, effective_to: e.target.value })} />
            </div>
            <FormTextarea label="Notes" rows={2} value={fee.notes} onChange={e => setFee({ ...fee, notes: e.target.value })} />
            {problem && <p role="alert" className="form-error-text">{problem}</p>}
          </>
        )}
      </Modal>
    </>
  );
};

export default ShippingAgentRatesFees;
